import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import styles from "./AnalyticsPage.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Ticket, 
  Clock,
  Filter,
  Download,
  Percent,
  Activity,
  SearchIcon,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { globalContext } from '../../Provider/AppProvider';
import { getDateRangeForPeriod, useTrendsData } from '../TrendsPage/hooks/useTrendsData';
import { GET_USER_PRICING_MODES, UserPricingModeRow } from '../TrendsPage/api/TrendsPageQuery';
import { GET_ALL_LEAGUES } from '../TeamUpdate/api/TeamUpdateQueries';


const GET_ALL_USER_PREFERENCES = gql`
  query GetAllUserPreferences {
    user_preference {
      u_id
      lc_id
      user {
        u_id
        u_email_id
        u_full_name
        u_first_name
        u_last_name
        u_deleted_at
        u_is_active
      }
    }
  }
`;

const GET_SPORTS_TEAM_CATEGORIES = gql`
  query GetSportsTeamCategories($leagueIds: [uuid!]!) {
    league_categories(
      where: {
        l_id: { _in: $leagueIds }
        lc_deleted_at: { _is_null: true }
        lc_is_active: { _eq: true }
      }
      order_by: { lc_name: asc }
    ) {
      lc_id
      lc_abbrev
      lc_name
      lc_colour_code
      l_id
      lc_team_type
      lc_ticketmaster_name
    }
  }
`;

interface TeamData {
  id: string;
  team: string;
  league: string;
  leagueId?: string;
  usersCount: number;
  userDetails: Array<{name: string; email: string}>;
  active: number;
  listed: number;
  sold: number;
  salePrice: number;
  paidOut: number;
  potential: number;
}

interface LeagueOption {
  id: string;
  name: string;
}

const normalizeLeagueName = (name: string): string => {
  const map: Record<string, string> = {
    'NCAA FB': 'NCAAFB',
    'NCAA BB': 'NCAAM',
    'NCAA W': 'NCAAW',
    'NCAAM': 'NCAAM',
    'NCAAW': 'NCAAW',
    'NCAAFB': 'NCAAFB',
  };
  return map[name] || name;
};

const isSportsLeagueType = (leagueType?: string | null) =>
  (leagueType || '').trim() === 'Sports';

const isTeamCategory = (teamType?: string | null) =>
  (teamType || '').trim() === 'Professional';

const isRealTeamRow = (team: {
  lc_name?: string | null;
  lc_team_type?: string | null;
  lc_ticketmaster_name?: string | null;
}) => {
  if (!isTeamCategory(team.lc_team_type)) return false;
  if (!String(team.lc_ticketmaster_name ?? '').trim()) return false;

  const name = String(team.lc_name ?? '').trim();
  const lower = name.toLowerCase();
  if (!name || lower === 'team tbd' || lower === 'tbd') return false;
  if (/\s+vs\.?\s+/i.test(name) || /\s+@\s+/.test(name) || /\s+at\s+/i.test(name)) return false;

  return true;
};

const matchesTeamEvent = (eventName: string, teamName: string, teamAbbrev: string) => {
  const event = eventName.toLowerCase();
  const name = teamName.toLowerCase().trim();
  const abbrev = teamAbbrev.toLowerCase().trim();

  if (name && event.includes(name)) return true;
  if (abbrev.length >= 3 && event.includes(abbrev)) return true;
  return false;
};

const getDbLeagues = (data?: { leagues?: any[]; league?: any[] }) =>
  data?.leagues ?? data?.league ?? [];

const AnalyticsPage = () => {
  const [viewMode, setViewMode] = useState<'web' | 'mobile'>('web');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30D');
  const [timeframeType, setTimeframeType] = useState<'preset' | 'custom'>('preset');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [selectedPattern, setSelectedPattern] = useState('All Patterns');
  const [selectedPriceChg, setSelectedPriceChg] = useState('All Price Chg');
  const [userSaleTimingDetailEmail, setUserSaleTimingDetailEmail] = useState<string | null>(null);
  const [leadTimeViewMode, setLeadTimeViewMode] = useState<'overview' | 'byLeague'>('overview');
  const [showLeadTimeFilters, setShowLeadTimeFilters] = useState(false);
  const [leadTimeLeague, setLeadTimeLeague] = useState('All Leagues');
  const [analyticsSearchQuery, setAnalyticsSearchQuery] = useState('');
  const [teamsSearchQuery, setTeamsSearchQuery] = useState('');
  const [topPerformersCount, setTopPerformersCount] = useState(10);
  const [highDelistCount, setHighDelistCount] = useState(10);
  const [priceMovementLeagueFilter, setPriceMovementLeagueFilter] = useState('All Leagues');
  const [priceMovementDateFilter, setPriceMovementDateFilter] = useState('');
  const [teamsLeagueFilter, setTeamsLeagueFilter] = useState('All Leagues');
  const [selectedMarketplaceTeams, setSelectedMarketplaceTeams] = useState<Set<string>>(new Set());
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [showFilterDatePicker, setShowFilterDatePicker] = useState(false);
  
  const [teamsStartDate, setTeamsStartDate] = useState<string>('');
  const [teamsEndDate, setTeamsEndDate] = useState<string>('');
  const [marketplaceStartDate, setMarketplaceStartDate] = useState<string>('');
  const [marketplaceEndDate, setMarketplaceEndDate] = useState<string>('');
  const [selectedTeamUsersModal, setSelectedTeamUsersModal] = useState<{ teamName: string; users: Array<{name: string; email: string}> } | null>(null);
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  const [showGlobalDatePicker, setShowGlobalDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  const refreshReloadLockRef = useRef(false);

  const { globalState } = useContext<any>(globalContext);
  const userId = globalState?.profile?.id;
  const userRole = globalState?.profile?.userRole;
  const userEmail =
    globalState?.profile?.mailId ||
    globalState?.profile?.email ||
    globalState?.profile?.u_email_id ||
    globalState?.profile?.u_email ||
    '';

  const handleTimeframeChange = (value: string) => {
    if (value === 'Custom') {
      setTimeframeType('custom');
      setSelectedTimeframe('Custom');
    } else {
      setTimeframeType('preset');
      setSelectedTimeframe(value);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const applyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      
      if (selectedFilter !== 'All') {
        setFilterStartDate(tempStartDate);
        setFilterEndDate(tempEndDate);
      }
      
      setTimeframeType('custom');
      setSelectedTimeframe('Custom');
      setShowGlobalDatePicker(false);
    } else {
      alert('Please select both start and end dates.');
    }
  };

  const trendsPeriod = useMemo(() => {
    if (timeframeType === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    const mappedPeriod = selectedTimeframe === '12M' ? 'All' : selectedTimeframe;
    return getDateRangeForPeriod(mappedPeriod);
  }, [selectedTimeframe, timeframeType, customStartDate, customEndDate]);

  const {
    chartData,
    leagueData,
    channelTickets,
    organizationStats,
    revenueAggregate,
    ticketTimeline,
    loading,
  } = useTrendsData({
    startDate: trendsPeriod.startDate,
    endDate: trendsPeriod.endDate,
    userId,
    userRole,
    userEmail,
    timelineActivityDateFilter: true,
  });

  const marketplaceData = useMemo(() => {
    const totals = new Map<string, { tickets: number; revenue: number; paid: number; profit: number }>();

    if (!channelTickets || channelTickets.length === 0) {
      return [];
    }

    let filteredChannelTickets = channelTickets;
    if (marketplaceStartDate || marketplaceEndDate) {
      filteredChannelTickets = channelTickets.filter((item: any) => {
        const soldDate = item?.created_at ? new Date(item.created_at) : null;
        if (!soldDate || Number.isNaN(soldDate.getTime())) return false;

        if (marketplaceStartDate) {
          const start = new Date(`${marketplaceStartDate}T00:00:00.000`);
          if (soldDate < start) return false;
        }

        if (marketplaceEndDate) {
          const end = new Date(`${marketplaceEndDate}T23:59:59.999`);
          if (soldDate > end) return false;
        }

        return true;
      });
    }

    const inferFromListingId = (listingId?: string): string | null => {
      if (!listingId) return null;
      const id = listingId.toLowerCase().trim();
      if (id.includes('ticketmaster') || id.includes('tm-')) return 'Ticketmaster';
      if (id.includes('stubhub') || id.includes('sh-')) return 'StubHub';
      if (id.includes('seatgeek') || id.includes('sg-')) return 'SeatGeek';
      if (id.includes('vivid') || id.includes('vs-')) return 'Vivid Seats';
      if (id.includes('tickpick') || id.includes('tp-')) return 'TickPick';
      if (id.includes('gametime') || id.includes('gt-')) return 'Gametime';
      return null;
    };

    const normalizeMarketplaceName = (value: any) => {
      const raw = String(value ?? '').trim();
      const lower = raw.toLowerCase();
      if (!lower || lower === 'null' || lower === 'undefined') return 'Other';
      if (lower.includes('ticketmaster')) return 'Ticketmaster';
      if (lower.includes('stubhub')) return 'StubHub';
      if (lower.includes('seatgeek')) return 'SeatGeek';
      if (lower.includes('vivid')) return 'Vivid Seats';
      if (lower.includes('tickpick')) return 'TickPick';
      if (lower.includes('gametime')) return 'Gametime';
      if (lower.includes('direct') || lower.includes('manual')) return 'Direct Sale';
      return raw;
    };

    filteredChannelTickets.forEach((item: any) => {
      let marketplace =
        item?.tp_channel ||
        item?.tp_marketplace ||
        item?.channel ||
        item?.marketplace ||
        item?.platform ||
        item?.source ||
        item?.listing_channel ||
        item?.listing_marketplace ||
        item?.sale_channel ||
        item?.source_name ||
        '';

      if (!marketplace || marketplace === 'null' || marketplace === 'undefined' || marketplace === 'Other') {
        const inferred = inferFromListingId(item?.tp_vl_listing_id || item?.listing_id);
        if (inferred) marketplace = inferred;
      }

      const normalized = normalizeMarketplaceName(marketplace);

      const status = (item?.status || '').toString().toLowerCase();
      if (!status.includes('sold') && !status.includes('settled') && !status.includes('complete') && !status.includes('paid')) return;

      const salePrice = Number(item?.paid ?? item?.price ?? 0);
      const paidOut = Number(item?.price ?? 0);
      const actualProfit = Number(item?.profit ?? 0);
      const resolvedProfit = actualProfit || Math.max(0, salePrice - paidOut);
      if (salePrice <= 0) return;

      const existing = totals.get(normalized) || { tickets: 0, revenue: 0, paid: 0, profit: 0 };
      existing.tickets += 1;
      existing.revenue += salePrice;
      existing.paid += paidOut;
      existing.profit += resolvedProfit;
      totals.set(normalized, existing);
    });

    const colors: Record<string, string> = {
      'StubHub': '#f97316',
      'Ticketmaster': '#3b82f6',
      'SeatGeek': '#f59e0b',
      'Vivid Seats': '#8b5cf6',
      'Gametime': '#ec4899',
      'TickPick': '#10b981',
      'Direct Sale': '#6b7280',
      'Other': '#9ca3af',
    };

    const finalData = Array.from(totals.entries()).map(([name, data]) => ({
      name,
      tickets: data.tickets,
      revenue: data.revenue,
      paid: Math.round(data.paid * 100) / 100,
      profit: Math.round(data.profit * 100) / 100,
      color: colors[name] || '#94a3b8',
    }));

    return finalData.sort((a, b) => b.revenue - a.revenue);
  }, [channelTickets, marketplaceStartDate, marketplaceEndDate]);

  const formatUserLabel = (value: string) => {
    if (!value) {
      return 'Unknown User';
    }
    return `User ${value.slice(0, 8)}`;
  };

  const percentChange = (firstValue: number, latestValue: number) => {
    if (!firstValue) {
      return 0;
    }
    return Number((((latestValue - firstValue) / firstValue) * 100).toFixed(1));
  };

  const getTicketQuantity = (ticket: any) => {
    const groupedCount = Array.isArray(ticket?.grouped_ticket_data) ? ticket.grouped_ticket_data.length : 0;
    return groupedCount > 0 ? groupedCount : 1;
  };

  const getTicketStatus = (ticket: any) =>
    String(ticket?.tp_mobile_display_status || ticket?.tp_status || '').toLowerCase().trim();

  const isSoldTicketStatus = (status: string) =>
    status.includes('sold') ||
    status.includes('settled') ||
    status.includes('complete') ||
    status.includes('paid') ||
    status.includes('finished') ||
    status.includes('confirmed');

  const isActiveListedTicketStatus = (status: string) =>
    (status === 'listed' || status === 'list' || status.includes('active')) &&
    !status.includes('delist') &&
    !isSoldTicketStatus(status);

  const selectedPricingFilter = useMemo(() => {
    if (selectedFilter === 'Self Pricing') return 'self-pricing';
    if (selectedFilter === 'Concierge') return 'concierge';
    return null;
  }, [selectedFilter]);

  const timelineUserIds = useMemo(
    () => Array.from(new Set((ticketTimeline || []).map((ticket) => ticket.user_id).filter(Boolean))),
    [ticketTimeline]
  );

  const { data: userPricingModesData, loading: userPricingModesLoading } = useQuery<{ user: UserPricingModeRow[] }>(GET_USER_PRICING_MODES, {
    variables: { userIds: timelineUserIds },
    skip: timelineUserIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const normalizePricingMode = (mode?: string | null): 'self-pricing' | 'concierge' | null => {
    if (!mode) return null;
    const normalized = String(mode).toLowerCase().trim();
    if (normalized === 'self-pricing' || normalized === 'self_pricing' || normalized === 'selfpricing') {
      return 'self-pricing';
    }
    if (normalized === 'concierge') return 'concierge';
    return null;
  };

  const pricingModeByUserId = useMemo(() => {
    const map = new Map<string, 'self-pricing' | 'concierge' | null>();
    (userPricingModesData?.user || []).forEach((user) => {
      const resolvedMode = normalizePricingMode(user.u_pricing_mode) ?? normalizePricingMode(user.u_client_type);
      map.set(user.u_id, resolvedMode);
    });
    return map;
  }, [userPricingModesData]);

  const getTicketPricingMode = (ticket: any): 'self-pricing' | 'concierge' | null => {
    return pricingModeByUserId.get(ticket.user_id) ?? null;
  };

  const isPricingModeLoadingInitial = timelineUserIds.length > 0 && userPricingModesLoading && pricingModeByUserId.size === 0;

  const payoutScopedTimeline = useMemo(() => {
    let filtered = ticketTimeline;

    if (selectedPricingFilter) {
      filtered = filtered.filter((ticket) => {
        const mode = getTicketPricingMode(ticket);
        if (selectedPricingFilter === 'self-pricing') {
          return mode === 'self-pricing';
        }
        return mode === 'concierge';
      });
    }

    if (filterStartDate || filterEndDate) {
      filtered = filtered.filter((ticket) => {
        const ticketDate = ticket.latest_update ? new Date(ticket.latest_update) : null;
        if (!ticketDate) return true;

        if (filterStartDate) {
          const startDate = new Date(filterStartDate);
          if (ticketDate < startDate) return false;
        }

        if (filterEndDate) {
          const endDate = new Date(filterEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (ticketDate > endDate) return false;
        }

        return true;
      });
    }

    return filtered;
  }, [selectedPricingFilter, ticketTimeline, pricingModeByUserId, filterStartDate, filterEndDate]);

  const scopedTimeline = useMemo(() => {
    const q = analyticsSearchQuery.trim().toLowerCase();
    if (!q) {
      return payoutScopedTimeline;
    }
    return payoutScopedTimeline.filter((ticket) => {
      const hay = [
        ticket.league_name,
        ticket.event_name,
        ticket.user_name,
        ticket.user_email,
        ticket.section,
        ticket.row,
        ticket.seat_range,
        ticket.tp_vl_listing_id,
        ticket.ticket_id,
        ticket.event_date,
      ]
        .map((p) => String(p ?? '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }, [analyticsSearchQuery, payoutScopedTimeline]);

  const derivedMarketplaceData = useMemo(() => {
    let filtered = scopedTimeline;
    if (marketplaceStartDate || marketplaceEndDate) {
      filtered = filtered.filter((ticket) => {
        const ticketDate = ticket.latest_update ? new Date(ticket.latest_update) : null;
        if (!ticketDate) return true;
        if (marketplaceStartDate && ticketDate < new Date(marketplaceStartDate)) return false;
        if (marketplaceEndDate) {
          const end = new Date(marketplaceEndDate);
          end.setHours(23, 59, 59, 999);
          if (ticketDate > end) return false;
        }
        return true;
      });
    }

    const marketplaceMap = new Map<string, {
      name: string;
      tickets: number;
      revenue: number;
      paid: number;
      profit: number;
      color: string;
    }>();

    const marketplaceColors: Record<string, string> = {
      'Ticketmaster': '#4f8ef7',
      'StubHub': '#f97316',
      'SeatGeek': '#10b981',
      'Vivid Seats': '#8b5cf6',
      'Gametime': '#ec4899',
      'TickPick': '#14b8a6',
      'Direct Sale': '#6b7280',
      'Other': '#9ca3af',
    };

    const inferFromListingId = (listingId?: string): string | null => {
      if (!listingId) return null;
      const id = listingId.toLowerCase().trim();
      if (id.includes('ticketmaster') || id.includes('tm-')) return 'Ticketmaster';
      if (id.includes('stubhub') || id.includes('sh-')) return 'StubHub';
      if (id.includes('seatgeek') || id.includes('sg-')) return 'SeatGeek';
      if (id.includes('vivid') || id.includes('vs-')) return 'Vivid Seats';
      if (id.includes('tickpick') || id.includes('tp-')) return 'TickPick';
      if (id.includes('gametime') || id.includes('gt-')) return 'Gametime';
      return null;
    };

    const normalizeMarketplaceName = (value: any) => {
      const raw = String(value ?? '').trim();
      const lower = raw.toLowerCase();
      if (!lower || lower === 'null' || lower === 'undefined') return 'Other';
      if (lower.includes('ticketmaster')) return 'Ticketmaster';
      if (lower.includes('stubhub')) return 'StubHub';
      if (lower.includes('seatgeek')) return 'SeatGeek';
      if (lower.includes('vivid')) return 'Vivid Seats';
      if (lower.includes('tickpick')) return 'TickPick';
      if (lower.includes('gametime')) return 'Gametime';
      if (lower.includes('direct') || lower.includes('manual')) return 'Direct Sale';
      return raw;
    };

    filtered.forEach((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      const isSold = status.includes('sold') || status.includes('settled') || 
                     status.includes('complete') || status.includes('paid') || 
                     status.includes('finished') || status.includes('confirmed');
      if (!isSold) return;

      const soldAmount = Number(ticket.tp_sold_amount ?? 0);
      const listPrice = Number(ticket.tp_list_price ?? 0);
      const finalPrice = soldAmount > 0 ? soldAmount : listPrice;
      if (finalPrice <= 0) return;

      const ticketAny = ticket as any;
      let marketplace =
        ticketAny.tp_channel ||
        ticketAny.tp_marketplace ||
        ticketAny.channel ||
        ticketAny.listing_channel ||
        ticketAny.tp_source ||
        ticketAny.source ||
        ticketAny.listing_source ||
        ticketAny.marketplace_name ||
        ticketAny.marketplace ||
        ticketAny.platform ||
        ticketAny.source_name ||
        ticketAny.sale_channel ||
        '';

      if (!marketplace || marketplace === 'null' || marketplace === 'undefined' || marketplace === 'Other') {
        const inferred = inferFromListingId(ticketAny.tp_vl_listing_id || ticketAny.listing_id);
        if (inferred) marketplace = inferred;
      }

      const normalized = normalizeMarketplaceName(marketplace);

      const qty = getTicketQuantity(ticket);
      const paidOutAmount = Number(ticket.tp_logitix_amount ?? 0);
      const profit = Math.max(0, finalPrice - paidOutAmount);

      const existing = marketplaceMap.get(normalized);
      if (existing) {
        existing.tickets += qty;
        existing.revenue += finalPrice;
        existing.paid += paidOutAmount;
        existing.profit += profit;
      } else {
        marketplaceMap.set(normalized, {
          name: normalized,
          tickets: qty,
          revenue: finalPrice,
          paid: paidOutAmount,
          profit: profit,
          color: marketplaceColors[normalized] || '#9ca3af',
        });
      }
    });

    return Array.from(marketplaceMap.values()).sort((a, b) => b.revenue - a.revenue);
  }, [scopedTimeline, marketplaceStartDate, marketplaceEndDate]);

  const finalMarketplaceData = useMemo(() => {
    const hasDateFilter = marketplaceStartDate || marketplaceEndDate;
    
    if (marketplaceData && marketplaceData.length > 0) {
      return marketplaceData;
    }

    if (hasDateFilter) {
      return [];
    }
    
    if (derivedMarketplaceData && derivedMarketplaceData.length > 0) {
      return derivedMarketplaceData;
    }
    
    if (scopedTimeline.length > 0) {
      const totalRevenue = scopedTimeline.reduce((sum, t) => {
        const sold = Number(t.tp_sold_amount ?? 0) || Number(t.tp_list_price ?? 0);
        return sum + sold;
      }, 0);
      const totalTickets = scopedTimeline.reduce((sum, t) => sum + getTicketQuantity(t), 0);
      const profit = totalRevenue * 0.08;
      const paid = totalRevenue - profit;
      return [{
        name: 'Other',
        tickets: totalTickets,
        revenue: totalRevenue,
        paid: paid,
        profit: profit,
        color: '#9ca3af',
      }];
    }
    return [];
  }, [marketplaceData, derivedMarketplaceData, scopedTimeline, marketplaceStartDate, marketplaceEndDate]);

  const { data: allLeaguesData, loading: allLeaguesLoading } = useQuery(GET_ALL_LEAGUES, {
    fetchPolicy: 'network-only',
  });

  const { data: userPreferencesData, loading: userPreferencesLoading } = useQuery(GET_ALL_USER_PREFERENCES, {
    fetchPolicy: 'cache-and-network',
  });

  const leagueNameById = useMemo(() => {
    const mapping = new Map<string, string>();
    getDbLeagues(allLeaguesData).forEach((l: any) => {
      const leagueName = l.l_name;
      if (l.l_id && leagueName) {
        mapping.set(l.l_id, normalizeLeagueName(leagueName.trim()));
      }
    });
    return mapping;
  }, [allLeaguesData]);

  const sportsLeagueIds = useMemo(() => {
    const ids = new Set<string>();
    getDbLeagues(allLeaguesData).forEach((l: any) => {
      if (l.l_id && isSportsLeagueType(l.l_type)) {
        ids.add(l.l_id);
      }
    });
    return ids;
  }, [allLeaguesData]);

  const sportsLeagueIdList = useMemo(
    () => Array.from(sportsLeagueIds),
    [sportsLeagueIds]
  );

  const { data: sportsTeamsData, loading: sportsTeamsLoading } = useQuery(GET_SPORTS_TEAM_CATEGORIES, {
    variables: { leagueIds: sportsLeagueIdList },
    skip: sportsLeagueIdList.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const teamsData = useMemo<TeamData[]>(() => {
    if (!sportsTeamsData?.league_categories) {
      return [];
    }

    const teamDetailsMap = new Map();
    sportsTeamsData.league_categories.forEach((team: any) => {
      if (!isRealTeamRow(team)) return;

      const leagueName = leagueNameById.get(team.l_id) || 'Unknown League';
      const normalizedLeague = normalizeLeagueName(leagueName);
      
      teamDetailsMap.set(team.lc_id, {
        id: team.lc_id,
        team: team.lc_name || team.lc_abbrev || 'Unknown Team',
        league: normalizedLeague,
        leagueId: team.l_id,
        abbrev: team.lc_abbrev || '',
        color: team.lc_colour_code || '#6b7280',
      });
    });

    const teamUserMap = new Map<string, Array<{name: string; email: string}>>();

    (userPreferencesData?.user_preference || []).forEach((pref: any) => {
      const teamId = pref.lc_id;
      if (!teamDetailsMap.has(teamId)) return;
      
      const user = pref.user || {};
      const isDeleted = user?.u_deleted_at !== null && user?.u_deleted_at !== undefined;
      const isInactive = user?.u_is_active === false;
      
      if (isDeleted || isInactive) return;
      
      const userName = user.u_full_name || user.u_first_name || user.u_email_id || formatUserLabel(pref.u_id);
      const userEmail = user.u_email_id || `${pref.u_id?.slice(0, 8) || 'unknown'}@quickasyst.local`;
      
      if (!teamUserMap.has(teamId)) {
        teamUserMap.set(teamId, []);
      }
      const userList = teamUserMap.get(teamId)!;
      if (!userList.some(u => u.email === userEmail)) {
        userList.push({ name: userName, email: userEmail });
      }
    });

    const teams: TeamData[] = [];

    teamDetailsMap.forEach((teamDetails, teamId) => {
      const users = teamUserMap.get(teamId) || [];
      
      let active = 0, listed = 0, sold = 0, salePrice = 0, paidOut = 0, potential = 0;
      
      let timelineToUse = scopedTimeline;
      
      if (teamsStartDate || teamsEndDate) {
        timelineToUse = scopedTimeline.filter((ticket) => {
          const ticketDate = ticket.latest_update ? new Date(ticket.latest_update) : null;
          if (!ticketDate) return true;
          
          if (teamsStartDate) {
            const startDate = new Date(teamsStartDate);
            if (ticketDate < startDate) return false;
          }
          
          if (teamsEndDate) {
            const endDate = new Date(teamsEndDate);
            endDate.setHours(23, 59, 59, 999);
            if (ticketDate > endDate) return false;
          }
          
          return true;
        });
      }
      
      timelineToUse.forEach((ticket) => {
        const ticketLeague = normalizeLeagueName(ticket.league_name || 'Unknown League');
        if (ticketLeague !== teamDetails.league) {
          return;
        }

        const eventName = ticket.event_name || '';
        
        if (matchesTeamEvent(eventName, teamDetails.team, teamDetails.abbrev)) {
          const quantity = getTicketQuantity(ticket);
          const listPrice = Number(ticket.tp_list_price ?? 0);
          const soldAmount = Number(ticket.tp_sold_amount ?? 0);
          const paidOutAmount = Number(ticket.tp_logitix_amount ?? 0);
          const status = getTicketStatus(ticket);
          
          if (isActiveListedTicketStatus(status)) active += quantity;
          if (isSoldTicketStatus(status)) sold += quantity;
          listed += listPrice;
          salePrice += soldAmount;
          paidOut += paidOutAmount;
          potential += Math.max(0, listPrice - paidOutAmount);
        }
      });

      teams.push({
        id: teamId,
        team: teamDetails.team,
        league: teamDetails.league,
        leagueId: teamDetails.leagueId,
        usersCount: users.length,
        userDetails: users,
        active,
        listed,
        sold,
        salePrice,
        paidOut,
        potential,
      });
    });

    return teams.sort((a, b) => b.usersCount - a.usersCount || a.team.localeCompare(b.team));
  }, [sportsTeamsData, userPreferencesData, scopedTimeline, teamsStartDate, teamsEndDate, leagueNameById]);

  const availableLeaguesForTeams = useMemo<LeagueOption[]>(() => {
    const leagueMap = new Map<string, string>();
    const hasSportsFilter = sportsLeagueIds.size > 0;
    
    getDbLeagues(allLeaguesData).forEach((l: any) => {
      const leagueName = l.l_name;
      if (!l.l_id || !leagueName) return;
      if (hasSportsFilter && !isSportsLeagueType(l.l_type)) return;
      leagueMap.set(l.l_id, normalizeLeagueName(leagueName.trim()));
    });
    
    teamsData.forEach((team) => {
      if (
        team.leagueId &&
        team.league &&
        team.league !== 'Unknown League' &&
        (!hasSportsFilter || sportsLeagueIds.has(team.leagueId))
      ) {
        leagueMap.set(team.leagueId, team.league);
      }
    });
    
    const leagues = Array.from(leagueMap, ([id, name]) => ({ id, name }))
      .filter(({ id }) => !hasSportsFilter || sportsLeagueIds.has(id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [{ id: 'All Leagues', name: 'All Leagues' }, ...leagues];
  }, [allLeaguesData, teamsData, sportsLeagueIds]);

  useEffect(() => {
    if (
      teamsLeagueFilter !== 'All Leagues' &&
      !availableLeaguesForTeams.some((league) => league.id === teamsLeagueFilter)
    ) {
      setTeamsLeagueFilter('All Leagues');
    }
  }, [teamsLeagueFilter, availableLeaguesForTeams]);

  const filteredTeamsData = useMemo<TeamData[]>(() => {
    let filtered =
      sportsLeagueIds.size > 0
        ? teamsData.filter(
            (team) => Boolean(team.leagueId) && sportsLeagueIds.has(team.leagueId!)
          )
        : teamsData;
    
    if (teamsLeagueFilter && teamsLeagueFilter !== 'All Leagues') {
      const filterLower = teamsLeagueFilter.toLowerCase().trim();
      filtered = filtered.filter((team) => {
        const teamLeagueId = String(team.leagueId || '').toLowerCase().trim();
        const teamLeague = String(team.league || '').toLowerCase().trim();
        
        return teamLeagueId === filterLower || teamLeague === filterLower;
      });
    }
    
    // Apply search query filter
    const query = teamsSearchQuery.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((team) => 
        String(team.team || '').toLowerCase().includes(query) ||
        String(team.league || '').toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [teamsSearchQuery, teamsData, teamsLeagueFilter, sportsLeagueIds]);

  const teamTotals = useMemo(() => {
    if (!filteredTeamsData.length) {
      return { users: 0, active: 0, listed: 0, sold: 0, salePrice: 0, paidOut: 0, potential: 0 };
    }
    
    const uniqueUsers = new Set<string>();
    filteredTeamsData.forEach((team) => {
      team.userDetails.forEach((user) => {
        uniqueUsers.add(user.email);
      });
    });
    
    return filteredTeamsData.reduce((acc, team) => ({
      users: uniqueUsers.size,
      active: acc.active + team.active,
      listed: acc.listed + team.listed,
      sold: acc.sold + team.sold,
      salePrice: acc.salePrice + team.salePrice,
      paidOut: acc.paidOut + team.paidOut,
      potential: acc.potential + team.potential
    }), { users: 0, active: 0, listed: 0, sold: 0, salePrice: 0, paidOut: 0, potential: 0 });
  }, [filteredTeamsData]);

  const handleExportTeams = () => {
    if (!filteredTeamsData.length) return;

    const headers = [
      'TEAM', 'LEAGUE', '# USERS', 'ACTIVE TICKETS', 'TOTAL LISTED',
      '# TICKETS SOLD', 'TOTAL SALE PRICE', 'TOTAL PAID OUT', 'POTENTIAL PAYOUT',
    ];

    const rows = filteredTeamsData.map((team) => [
      team.team, team.league, team.usersCount, team.active, team.listed,
      team.sold, team.salePrice, team.paidOut, team.potential,
    ]);

    rows.push([
      'Total', '', teamTotals.users, teamTotals.active, teamTotals.listed,
      teamTotals.sold, teamTotals.salePrice, teamTotals.paidOut, teamTotals.potential,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `teams-selected-by-users-${selectedTimeframe.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ========== REMAINING ANALYTICS ==========
  const analyticsEmptyHint = useMemo(() => {
    if (scopedTimeline.length > 0) {
      return null;
    }
    const sq = analyticsSearchQuery.trim();
    if (sq.length > 0 && payoutScopedTimeline.length > 0) {
      return `No tickets match your search. Clear the search box to use the ${payoutScopedTimeline.length} ticket row(s) loaded for this client type filter and timeframe.`;
    }
    if (sq.length > 0) {
      return `Nothing matches "${sq}" for ${selectedFilter} in this timeframe. Clear search, try All, or widen the range (e.g. 90D).`;
    }
    if (ticketTimeline.length === 0) {
      return 'No tickets returned from the server for this timeframe. Refresh re-fetches; if this stays empty, there is no data for this range.';
    }
    if (selectedFilter === 'Concierge') {
      return 'No Concierge tickets in this period. Try All or Self Pricing, or expand the timeframe.';
    }
    if (selectedFilter === 'Self Pricing') {
      return 'No Self Pricing tickets in this period. Try All or Concierge.';
    }
    return null;
  }, [
    analyticsSearchQuery,
    payoutScopedTimeline.length,
    scopedTimeline.length,
    selectedFilter,
    ticketTimeline.length,
  ]);

  const leadTimeTimelineForStats = useMemo(() => {
    if (leadTimeViewMode === 'byLeague' && leadTimeLeague !== 'All Leagues') {
      return scopedTimeline.filter((t) => t.league_name === leadTimeLeague);
    }
    return scopedTimeline;
  }, [scopedTimeline, leadTimeViewMode, leadTimeLeague]);

  const userProfileById = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    scopedTimeline.forEach((ticket) => {
      const key = ticket.user_id || '';
      if (!key || map.has(key)) return;
      const resolvedName = (ticket.user_name || '').trim() || formatUserLabel(key);
      const resolvedEmail = (ticket.user_email || '').trim() || `${key.slice(0, 8)}@quickasyst.local`;
      map.set(key, { name: resolvedName, email: resolvedEmail });
    });
    return map;
  }, [scopedTimeline]);

  useEffect(() => {
    const all = ticketTimeline ?? [];
    const concierge = all.filter((ticket) => {
      return getTicketPricingMode(ticket) === 'concierge';
    });
    const selfPricing = all.filter((ticket) => {
      return getTicketPricingMode(ticket) === 'self-pricing';
    });
    const uniquePricingModes = Array.from(
      new Set(
        all
          .map((ticket) => getTicketPricingMode(ticket))
          .filter((mode): mode is 'self-pricing' | 'concierge' => mode === 'self-pricing' || mode === 'concierge')
      )
    ).slice(0, 10);
    const unknownPricingModeCount = all.filter((ticket) => !getTicketPricingMode(ticket)).length;

    console.log('[ANALYTICS][FILTER_DEBUG]', {
      selectedFilter,
      allCount: all.length,
      selfPricingCount: selfPricing.length,
      conciergeCount: concierge.length,
      scopedCount: scopedTimeline.length,
      pricingModes: uniquePricingModes,
      unknownPricingModeCount,
    });
  }, [selectedFilter, ticketTimeline, scopedTimeline, pricingModeByUserId]);

  const orgData = useMemo(() => {
    const firstPoint = chartData[0];
    const latestPoint = chartData[chartData.length - 1];
    const now = Date.now();
    const in24Hours = 24 * 60 * 60 * 1000;
    const recentTickets = scopedTimeline.filter((ticket) => {
      const updatedAt = new Date(ticket.latest_update).getTime();
      return Number.isFinite(updatedAt) && now - updatedAt <= in24Hours;
    });
    const recentUsers = new Set(recentTickets.map((ticket) => ticket.user_id).filter(Boolean));

    const totals = scopedTimeline.reduce(
      (acc, row) => {
        const soldAmount = Number(row.tp_sold_amount ?? row.tp_list_price ?? 0);
        const qty = getTicketQuantity(row);
        acc.revenue += soldAmount;
        acc.profit += soldAmount * 0.08;
        acc.sold += qty;
        return acc;
      },
      { revenue: 0, profit: 0, sold: 0 }
    );
    const latestRevenue = totals.revenue;
    const latestProfit = totals.profit;
    const latestSold = totals.sold;
    const latestAvgPrice = latestSold > 0 ? Number((latestRevenue / latestSold).toFixed(2)) : 0;
    const distinctUsers = new Set(scopedTimeline.map((ticket) => ticket.user_id).filter(Boolean)).size;
    const listedCount = scopedTimeline.filter((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      return status.includes('list');
    }).length;
    const delistedCount = scopedTimeline.filter((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      return status.includes('delist');
    }).length;
    const delistRate = listedCount > 0 ? Number(((delistedCount / listedCount) * 100).toFixed(1)) : 0;

    return {
      totalUsers: distinctUsers,
      active24h: recentUsers.size,
      last24h: recentTickets.length,
      totalRevenue: latestRevenue,
      totalProfit: latestProfit,
      avgRevPerUser: distinctUsers > 0 ? Math.round(latestRevenue / distinctUsers) : 0,
      delistRate,
      ticketsSold: latestSold,
      avgTicketPrice: latestAvgPrice,
      revenueChange: percentChange(Number(firstPoint?.revenue ?? 0), Number(latestPoint?.revenue ?? 0)),
      profitChange: percentChange(
        Number(firstPoint?.qaProfit ?? firstPoint?.profit ?? 0),
        Number(latestPoint?.qaProfit ?? latestPoint?.profit ?? 0)
      ),
      soldChange: percentChange(Number(firstPoint?.sold ?? 0), Number(latestPoint?.sold ?? 0)),
    };
  }, [chartData, organizationStats, revenueAggregate, scopedTimeline, selectedPricingFilter]);

  const allLeaguesMaster = useMemo(() => {
    const staticLeagues = new Set<string>();
    
    // Get leagues from allLeaguesData query
    getDbLeagues(allLeaguesData).forEach((l: any) => {
      const leagueName = l.l_name;
      if (leagueName) {
        staticLeagues.add(normalizeLeagueName(leagueName));
      }
    });
    
    // Also get leagues from tickets
    (ticketTimeline || []).forEach(ticket => {
      if (ticket.league_name) {
        staticLeagues.add(normalizeLeagueName(ticket.league_name));
      }
    });
    
    // Add default leagues if empty
    if (staticLeagues.size === 0) {
      ['NBA', 'NFL', 'MLB', 'NHL', 'MLS', 'Concerts', 'Theater', 'NCAA FB', 'NCAA BB'].forEach(league => {
        staticLeagues.add(league);
      });
    }
    
    return Array.from(staticLeagues).sort();
  }, [ticketTimeline, allLeaguesData]);

  const leagueIcons: Record<string, string> = {
    NBA: '🏀',
    NFL: '🏈',
    MLB: '⚾',
    NHL: '🏒',
    MLS: '⚽',
    Concerts: '🎤',
    Theater: '🎭',
    'NCAA FB': '🏈',
    'NCAA BB': '🏀',
    NASCAR: '🏎️',
    Rodeo: '🤠',
    NCAAFB: '🏈',
    NCAAM: '🏀',
    NCAAW: '🏀',
  };
  
  const leagueColors: Record<string, string> = {
    NBA: '#f97316',
    NFL: '#3b82f6',
    MLB: '#22c55e',
    NHL: '#111111',
    MLS: '#ec4899',
    'NCAA FB': '#6366f1',
    'NCAA BB': '#f59e0b',
    Concerts: '#14b8a6',
    Theater: '#8b5cf6',
    NASCAR: '#ef4444',
    Rodeo: '#8b5cf6',
    NCAAFB: '#6366f1',
    NCAAM: '#f59e0b',
    NCAAW: '#ec4899',
  };

  const ticketPrices = useMemo(() => {
    const byLeague = new Map<string, { revenue: number; tickets: number }>();
    
    allLeaguesMaster.forEach(league => {
      byLeague.set(league, { revenue: 0, tickets: 0 });
    });
    
    scopedTimeline.forEach(ticket => {
      const league = normalizeLeagueName(ticket.league_name || 'Unknown');
      if (!byLeague.has(league)) byLeague.set(league, { revenue: 0, tickets: 0 });
      const row = byLeague.get(league)!;
      const amount = Number(ticket.tp_sold_amount ?? ticket.tp_list_price ?? 0);
      const qty = getTicketQuantity(ticket);
      row.revenue += amount;
      row.tickets += qty;
      byLeague.set(league, row);
    });
    
    return Array.from(byLeague.entries())
      .map(([league, row]) => ({
        league,
        price: row.tickets > 0 ? Math.round(row.revenue / row.tickets) : 0,
        sold: row.tickets,
        icon: leagueIcons[league] || '🎟️',
        color: leagueColors[league] || '#4f8ef7',
      }))
      .sort((a, b) => b.price - a.price);
  }, [scopedTimeline, allLeaguesMaster]);

  const clientTypeData = useMemo(() => {
    const selfPricingCount = scopedTimeline.filter((ticket) => getTicketPricingMode(ticket) === 'self-pricing').length;
    const conciergeCount = scopedTimeline.filter((ticket) => getTicketPricingMode(ticket) === 'concierge').length;
    const total = scopedTimeline.length || 1;

    return [
      {
        type: 'Self Pricing',
        count: selfPricingCount,
        percentage: Number(((selfPricingCount / total) * 100).toFixed(1)),
        color: '#4f8ef7',
      },
      {
        type: 'Concierge',
        count: conciergeCount,
        percentage: Number(((conciergeCount / total) * 100).toFixed(1)),
        color: '#16a34a',
      },
    ];
  }, [scopedTimeline, pricingModeByUserId]);

  const revenueByUser = useMemo(() => {
    const map = new Map<string, number>();
    scopedTimeline.forEach((ticket) => {
      const amount = Number(ticket.tp_sold_amount ?? ticket.tp_list_price ?? 0);
      const key = ticket.user_id || 'unknown';
      map.set(key, (map.get(key) ?? 0) + amount);
    });
    return map;
  }, [scopedTimeline]);

  const topPerformers = useMemo(() => {
    const soldByUser = new Map<string, number>();
    scopedTimeline.forEach((ticket) => {
      const key = ticket.user_id || 'unknown';
      soldByUser.set(key, (soldByUser.get(key) ?? 0) + getTicketQuantity(ticket));
    });

    const getTier = (revenue: number) => {
      if (revenue >= 20000) return { tier: 'VIP', tierColor: '#f97316' };
      if (revenue >= 10000) return { tier: 'Gold', tierColor: '#f59e0b' };
      if (revenue >= 5000) return { tier: 'Silver', tierColor: '#94a3b8' };
      return { tier: 'Bronze', tierColor: '#8b5cf6' };
    };

    return Array.from(revenueByUser.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, topPerformersCount)
      .map(([id, revenue], index) => {
        const profile = userProfileById.get(id);
        const userLabel = profile?.name || formatUserLabel(id);
        const initials = userLabel
          .split(' ')
          .slice(0, 2)
          .map((part) => part[0])
          .join('');
        const tierData = getTier(revenue);
        return {
          rank: index + 1,
          name: userLabel,
          email: profile?.email || `${id.slice(0, 8)}@quickasyst.local`,
          tier: tierData.tier,
          revenue,
          sold: soldByUser.get(id) ?? 0,
          avatar: initials,
          tierColor: tierData.tierColor,
        };
      });
  }, [revenueByUser, scopedTimeline, userProfileById, topPerformersCount]);

  const highDelistUsers = useMemo(() => {
    const totals = new Map<string, { total: number; delisted: number }>();
    scopedTimeline.forEach((ticket) => {
      const key = ticket.user_id || 'unknown';
      const entry = totals.get(key) ?? { total: 0, delisted: 0 };
      entry.total += 1;
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      if (status.includes('delist')) {
        entry.delisted += 1;
      }
      totals.set(key, entry);
    });

    return Array.from(totals.entries())
      .map(([id, value]) => {
        const rate = value.total > 0 ? Number(((value.delisted / value.total) * 100).toFixed(1)) : 0;
        const profile = userProfileById.get(id);
        return {
          name: profile?.name || formatUserLabel(id),
          email: profile?.email || `${id.slice(0, 8)}@quickasyst.local`,
          type: 'Self Pricing',
          delistRate: rate,
          delistCount: value.delisted,
        };
      })
      .filter((item) => item.delistCount > 0)
      .sort((a, b) => b.delistRate - a.delistRate)
      .slice(0, highDelistCount);
  }, [scopedTimeline, userProfileById, highDelistCount]);

  const leagueSalesBreakdown = useMemo(() => {
    const byLeague = new Map<string, { count: number; revenue: number }>();
    
    allLeaguesMaster.forEach(league => {
      byLeague.set(league, { count: 0, revenue: 0 });
    });
    
    scopedTimeline.forEach(ticket => {
      const league = normalizeLeagueName(ticket.league_name || 'Unknown');
      if (!byLeague.has(league)) byLeague.set(league, { count: 0, revenue: 0 });
      const row = byLeague.get(league)!;
      const qty = getTicketQuantity(ticket);
      const amount = Number(ticket.tp_sold_amount ?? ticket.tp_list_price ?? 0);
      row.count += qty;
      row.revenue += amount;
      byLeague.set(league, row);
    });
    
    return Array.from(byLeague.entries())
      .filter(([, row]) => row.count > 0)
      .map(([league, row]) => ({
        league,
        count: row.count,
        revenue: row.revenue,
        icon: leagueIcons[league] || '🎟️',
        color: leagueColors[league] || '#4f8ef7',
      }))
      .sort((a, b) => b.count - a.count);
  }, [scopedTimeline, allLeaguesMaster]);

  // ===== LEAD TIME ANALYTICS =====
  const leadTimeData = useMemo(() => {
    const parseDate = (value?: string | null) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const getDaysBeforeEvent = (ticket: any) => {
      const eventDate = parseDate(ticket?.event_date);
      const actionDate = parseDate(ticket?.latest_update);
      if (!eventDate || !actionDate) return null;
      return (eventDate.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24);
    };

    const isSold = (ticket: any) => {
      const status = String(ticket?.tp_mobile_display_status || ticket?.tp_status || '').toLowerCase();
      return status.includes('sold') || status.includes('settled') || status.includes('complete') || status.includes('paid');
    };

    const isListed = (ticket: any) => {
      const status = String(ticket?.tp_mobile_display_status || ticket?.tp_status || '').toLowerCase();
      return status.includes('list');
    };

    let soldDaysTotal = 0;
    let soldQtyTotal = 0;
    let soldBeforeDaysTotal = 0;
    let soldBeforeQtyTotal = 0;
    let listedDaysTotal = 0;
    let listedQtyTotal = 0;
    let priceDeltaTotal = 0;
    let priceDeltaQty = 0;

    const soldBuckets = [0, 0, 0, 0, 0];
    const soldBucketsByLeague = new Map<string, number[]>();
    const listingBuckets = [
      { timing: '3 Days Before Event', count: 0, priceTotal: 0 },
      { timing: '7 Days Before Event', count: 0, priceTotal: 0 },
      { timing: '1-2 Weeks Before Event', count: 0, priceTotal: 0 },
      { timing: '2-4 Weeks Before Event', count: 0, priceTotal: 0 },
      { timing: '1 Month Before Event', count: 0, priceTotal: 0 },
    ];

    const leagueAgg = new Map<string, { daysTotal: number; sold: number; priceDeltaTotal: number }>();

    leadTimeTimelineForStats.forEach((ticket) => {
      const days = getDaysBeforeEvent(ticket);
      if (days === null) return;

      const qty = getTicketQuantity(ticket);
      const soldAmount = Number(ticket.tp_sold_amount ?? 0);
      const listAmount = Number(ticket.tp_list_price ?? 0);
      const league = normalizeLeagueName(ticket.league_name || 'Unknown');

      if (isSold(ticket)) {
        soldDaysTotal += days * qty;
        soldQtyTotal += qty;
        if (days >= 0) {
          soldBeforeDaysTotal += days * qty;
          soldBeforeQtyTotal += qty;
        }

        if (days < 0) soldBuckets[4] += qty;
        else if (days <= 3) soldBuckets[0] += qty;
        else if (days <= 7) soldBuckets[1] += qty;
        else if (days <= 14) soldBuckets[2] += qty;
        else soldBuckets[3] += qty;

        const leagueBuckets = soldBucketsByLeague.get(league) ?? [0, 0, 0, 0, 0];
        if (days < 0) leagueBuckets[4] += qty;
        else if (days <= 3) leagueBuckets[0] += qty;
        else if (days <= 7) leagueBuckets[1] += qty;
        else if (days <= 14) leagueBuckets[2] += qty;
        else leagueBuckets[3] += qty;
        soldBucketsByLeague.set(league, leagueBuckets);

        if (days >= 0) {
          const lg = leagueAgg.get(league) ?? { daysTotal: 0, sold: 0, priceDeltaTotal: 0 };
          lg.daysTotal += days * qty;
          lg.sold += qty;
          lg.priceDeltaTotal += (soldAmount - listAmount) * qty;
          leagueAgg.set(league, lg);
        }
      }

      if (isListed(ticket) && days >= 0) {
        listedDaysTotal += days * qty;
        listedQtyTotal += qty;
        const effectivePrice = Number(ticket.tp_list_price ?? ticket.tp_sold_amount ?? 0);
        if (days <= 3) {
          listingBuckets[0].count += qty;
          listingBuckets[0].priceTotal += effectivePrice * qty;
        } else if (days <= 7) {
          listingBuckets[1].count += qty;
          listingBuckets[1].priceTotal += effectivePrice * qty;
        } else if (days <= 14) {
          listingBuckets[2].count += qty;
          listingBuckets[2].priceTotal += effectivePrice * qty;
        } else if (days <= 28) {
          listingBuckets[3].count += qty;
          listingBuckets[3].priceTotal += effectivePrice * qty;
        } else {
          listingBuckets[4].count += qty;
          listingBuckets[4].priceTotal += effectivePrice * qty;
        }
      }

      if (soldAmount > 0 && listAmount > 0) {
        priceDeltaTotal += (soldAmount - listAmount) * qty;
        priceDeltaQty += qty;
      }
    });

    const avgDaysBeforeSold = soldBeforeQtyTotal > 0 ? Number((soldBeforeDaysTotal / soldBeforeQtyTotal).toFixed(1)) : 0;
    const avgDaysBeforeListed = listedQtyTotal > 0 ? Number((listedDaysTotal / listedQtyTotal).toFixed(1)) : 0;
    const avgPriceChange = priceDeltaQty > 0 ? Number((priceDeltaTotal / priceDeltaQty).toFixed(2)) : 0;
    const totalTicketsAnalyzed = soldQtyTotal;
    const soldTotalForPercent = soldBuckets.reduce((acc, val) => acc + val, 0) || 1;
    const platformSaleTiming = soldBuckets.map((count) => Number(((count / soldTotalForPercent) * 100).toFixed(1)));

    const listingTotalCount = listingBuckets.reduce((acc, row) => acc + row.count, 0);
    const listingPctDenom = listingTotalCount || 1;
    const listingTimingDistribution = listingBuckets.map((row) => ({
      timing: row.timing,
      count: row.count,
      percentage: Number(((row.count / listingPctDenom) * 100).toFixed(1)),
      price: row.count > 0 ? Number((row.priceTotal / row.count).toFixed(2)) : 0,
    }));

    const leadTimeByLeague = Array.from(leagueAgg.entries())
      .filter(([, value]) => value.sold > 0)
      .map(([league, value]) => ({
        league,
        days: Number((value.daysTotal / value.sold).toFixed(1)),
      }))
      .sort((a, b) => b.days - a.days)
      .slice(0, 6);

    const timingByLeague = Array.from(leagueAgg.entries())
      .filter(([, value]) => value.sold > 0)
      .map(([league, value]) => {
        const avgDays = value.daysTotal / value.sold;
        const avgPriceDelta = value.priceDeltaTotal / value.sold;
        const sign = avgPriceDelta >= 0 ? '+' : '-';
        return {
          league,
          daysToSell: Number(avgDays.toFixed(1)),
          priceChg: `${sign}$${Math.abs(avgPriceDelta).toFixed(0)}`,
          sold: value.sold,
        };
      })
      .sort((a, b) => b.sold - a.sold)
      .slice(0, 8);

    return {
      soldLeadTime: `Overall tickets sell ~${avgDaysBeforeSold} days before the event on average (based on ${soldQtyTotal} sales).`,
      listedLeadTime: `Overall tickets list ~${avgDaysBeforeListed} days before the event on average (based on ${listedQtyTotal} listings).`,
      avgDaysBeforeSold,
      avgDaysBeforeListed,
      avgPriceChange,
      totalTicketsAnalyzed,
      leadTimeByLeague,
      timingByLeague,
      platformSaleTiming,
      platformSaleTimingByLeague: Object.fromEntries(soldBucketsByLeague),
      listingTimingDistribution,
      totalListings: listingTotalCount,
    };
  }, [leadTimeTimelineForStats]);

  const priceMovementData = useMemo(() => {
    const parseDate = (value?: string | null) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const buckets = new Map<string, { total: number; change: number; base: number; increased: number; decreased: number }>();
    const labels = ['3 Days Before Event', '7 Days Before Event', '1-2 Weeks Before Event', '2-4 Weeks Before Event'];
    labels.forEach((label) => buckets.set(label, { total: 0, change: 0, base: 0, increased: 0, decreased: 0 }));

    let filteredTimeline = leadTimeTimelineForStats;
    
    if (priceMovementLeagueFilter && priceMovementLeagueFilter !== 'All Leagues') {
      filteredTimeline = filteredTimeline.filter((t) => normalizeLeagueName(t.league_name || '') === priceMovementLeagueFilter);
    }
    
    if (priceMovementDateFilter) {
      const filterDate = parseDate(priceMovementDateFilter);
      if (filterDate) {
        filteredTimeline = filteredTimeline.filter((t) => {
          const actionDate = parseDate(t.latest_update);
          return actionDate && actionDate.getTime() >= filterDate.getTime();
        });
      }
    }

    filteredTimeline.forEach((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      if (!status.includes('sold') && !status.includes('settled') && !status.includes('complete') && !status.includes('paid')) return;
      const eventDate = parseDate(ticket.event_date);
      const actionDate = parseDate(ticket.latest_update);
      if (!eventDate || !actionDate) return;
      const days = (eventDate.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24);
      if (days < 0 || days > 28) return;

      const listAmount = Number(ticket.tp_list_price ?? 0);
      const soldAmount = Number(ticket.tp_sold_amount ?? 0);
      if (!(listAmount > 0 && soldAmount > 0)) return;

      const qty = getTicketQuantity(ticket);
      const delta = soldAmount - listAmount;
      const label = days <= 3 ? labels[0] : days <= 7 ? labels[1] : days <= 14 ? labels[2] : labels[3];
      const row = buckets.get(label)!;
      row.total += qty;
      row.change += delta * qty;
      row.base += listAmount * qty;
      if (delta >= 0) row.increased += qty;
      else row.decreased += qty;
      buckets.set(label, row);
    });

    return labels.map((label) => {
      const row = buckets.get(label)!;
      const avgChange = row.total > 0 ? row.change / row.total : 0;
      const avgChangePercent = row.base > 0 ? (row.change / row.base) * 100 : 0;
      return {
        timing: label,
        total: row.total,
        change: Number(avgChange.toFixed(2)),
        changePercent: Number(avgChangePercent.toFixed(1)),
        increased: row.increased,
        decreased: row.decreased,
      };
    });
  }, [leadTimeTimelineForStats, priceMovementLeagueFilter, priceMovementDateFilter]);

  const userSaleTimingData = useMemo(() => {
    const parseDate = (value?: string | null) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const byUser = new Map<
      string,
      { sold: number; daysTotal: number; deltaSum: number; baseSum: number }
    >();
    leadTimeTimelineForStats.forEach((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      if (!status.includes('sold') && !status.includes('settled') && !status.includes('complete') && !status.includes('paid')) return;
      const eventDate = parseDate(ticket.event_date);
      const actionDate = parseDate(ticket.latest_update);
      if (!eventDate || !actionDate) return;

      const qty = getTicketQuantity(ticket);
      const days = (eventDate.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24);
      if (days < 0) return;
      const listAmount = Number(ticket.tp_list_price ?? 0);
      const soldAmount = Number(ticket.tp_sold_amount ?? 0);
      const id = ticket.user_id || 'unknown';

      const row = byUser.get(id) ?? { sold: 0, daysTotal: 0, deltaSum: 0, baseSum: 0 };
      row.sold += qty;
      row.daysTotal += days * qty;
      if (listAmount > 0 && soldAmount > 0) {
        row.deltaSum += (soldAmount - listAmount) * qty;
        row.baseSum += listAmount * qty;
      }
      byUser.set(id, row);
    });

    return Array.from(byUser.entries())
      .filter(([, row]) => row.sold > 0)
      .map(([id, row]) => {
        const profile = userProfileById.get(id);
        const avgDays = row.daysTotal / row.sold;
        const priceChange = row.baseSum > 0 ? (row.deltaSum / row.baseSum) * 100 : 0;
        const vs = Number((avgDays - leadTimeData.avgDaysBeforeSold).toFixed(1));
        return {
          name: profile?.name || formatUserLabel(id),
          email: profile?.email || `${id.slice(0, 8)}@quickasyst.local`,
          sold: row.sold,
          avgDays: Number(avgDays.toFixed(1)),
          vs,
          priceChange: Number(priceChange.toFixed(1)),
          pattern: avgDays <= leadTimeData.avgDaysBeforeSold ? 'week before seller' : 'early seller',
        };
      })
      .sort((a, b) => b.sold - a.sold)
      .slice(0, 12);
  }, [leadTimeData.avgDaysBeforeSold, leadTimeTimelineForStats, userProfileById]);

  const filteredUserSaleTimingData = useMemo(() => {
    return userSaleTimingData.filter((user) => {
      const patternOk =
        selectedPattern === 'All Patterns' ||
        (selectedPattern === 'Week before seller' && user.pattern === 'week before seller') ||
        (selectedPattern === 'Early seller' && user.pattern === 'early seller');

      const priceOk =
        selectedPriceChg === 'All Price Chg' ||
        (selectedPriceChg === 'Positive' && user.priceChange > 0) ||
        (selectedPriceChg === 'Negative' && user.priceChange < 0);

      return patternOk && priceOk;
    });
  }, [selectedPattern, selectedPriceChg, userSaleTimingData]);

  useEffect(() => {
    setUserSaleTimingDetailEmail((prev) => {
      if (!prev) return null;
      if (!filteredUserSaleTimingData.length) return null;
      if (!filteredUserSaleTimingData.some((u) => u.email === prev)) return null;
      return prev;
    });
  }, [filteredUserSaleTimingData]);

  const userSaleTimingDetail = useMemo(() => {
    if (!filteredUserSaleTimingData.length) return null;
    if (userSaleTimingDetailEmail) {
      const match = filteredUserSaleTimingData.find((u) => u.email === userSaleTimingDetailEmail);
      if (match) return match;
    }
    return filteredUserSaleTimingData[0];
  }, [filteredUserSaleTimingData, userSaleTimingDetailEmail]);

  const leadTimeLeagueOptions = useMemo(() => {
    const parseDate = (value?: string | null) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const leagues = new Set<string>();

    scopedTimeline.forEach((ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      if (!status.includes('sold') && !status.includes('settled') && !status.includes('complete') && !status.includes('paid')) return;

      const eventDate = parseDate(ticket.event_date);
      const actionDate = parseDate(ticket.latest_update);
      if (!eventDate || !actionDate) return;

      const league = normalizeLeagueName(ticket.league_name || 'Unknown');
      leagues.add(league);
    });

    return ['All Leagues', ...Array.from(leagues).sort((a, b) => a.localeCompare(b))];
  }, [scopedTimeline]);

  const priceMovementLeagueOptions = useMemo(() => {
    const leagues = new Set<string>();
    leadTimeTimelineForStats.forEach((ticket) => {
      if (ticket.league_name) {
        leagues.add(normalizeLeagueName(ticket.league_name));
      }
    });
    return ['All Leagues', ...Array.from(leagues).sort((a, b) => a.localeCompare(b))];
  }, [leadTimeTimelineForStats]);

  useEffect(() => {
    if (!leadTimeLeagueOptions.includes(leadTimeLeague)) {
      setLeadTimeLeague('All Leagues');
    }
  }, [leadTimeLeague, leadTimeLeagueOptions]);

  const displayedPlatformSaleTiming = useMemo(() => leadTimeData.platformSaleTiming, [leadTimeData.platformSaleTiming]);

  const handleRefresh = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (refreshReloadLockRef.current) return;
    refreshReloadLockRef.current = true;
    requestAnimationFrame(() => {
      window.location.reload();
    });
  };

  const handleExportUserSaleTiming = () => {
    if (!filteredUserSaleTimingData.length) return;

    const headers = ['USER', 'EMAIL', 'SOLD', 'AVG DAYS', 'VS PLATFORM (DAYS)', 'PRICE CHANGE (%)', 'PATTERN'];
    const rows = filteredUserSaleTimingData.map((user) => [
      user.name, user.email, user.sold, user.avgDays, user.vs, user.priceChange, user.pattern,
    ]);

    rows.push(['Platform Avg (days)', '', '', leadTimeData.avgDaysBeforeSold, '', '', '']);

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `user-sale-timing-${selectedTimeframe.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    console.log('[ANALYTICS_PAGE][UI_DATA]', {
      selectedTimeframe, userId, userRole, orgData,
      leagueCards: ticketPrices.length, marketplaces: marketplaceData?.length || 0,
      topPerformers: topPerformers.length, highDelistUsers: highDelistUsers.length, teamsRows: teamsData.length,
      availableLeagues: availableLeaguesForTeams,
      filteredTeams: filteredTeamsData.length,
    });
  }, [selectedTimeframe, userId, userRole, orgData, ticketPrices.length, marketplaceData, topPerformers.length, highDelistUsers.length, teamsData.length, availableLeaguesForTeams, filteredTeamsData.length]);

  // ========== RENDER ==========
  return (
    <div className={`${styles.dashboard} ${viewMode === 'mobile' ? styles.mobileView : ''} ${isDarkMode ? styles.dashboardDark : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <p className={styles.logo}>Performance Overview</p>
            <span className={styles.headerBadge}>Organization Analytics</span>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search event, team, seat, user..."
                className={styles.searchInput}
                value={analyticsSearchQuery}
                onChange={(e) => setAnalyticsSearchQuery(e.target.value)}
              />
            </div>
            <div className={styles.iconGroup}>
              <button type="button" className={styles.iconBtn} onClick={handleRefresh} disabled={loading}>
                <RefreshRoundedIcon fontSize="small" className={loading ? styles.iconBtnSpinning : undefined} />
              </button>
              <button type="button" className={styles.iconBtn} onClick={() => setIsDarkMode((v) => !v)}>
                {isDarkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.filterSection}>
            <button className={styles.filterBtn} onClick={() => { setTempStartDate(customStartDate); setTempEndDate(customEndDate); setShowGlobalDatePicker(true); }}>
              <Filter size={14} /> Filter
            </button>
            <div className={styles.filterTabs}>
              {['All', 'Self Pricing', 'Concierge'].map((filter) => (
                <button
                  key={filter}
                  className={`${styles.filterTab} ${selectedFilter === filter ? styles.activeFilter : ''}`}
                  onClick={() => { setSelectedFilter(filter); setFilterStartDate(''); setFilterEndDate(''); }}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Global Date Filter Popup */}
      {showGlobalDatePicker && (
        <div className={styles.datePickerOverlay}>
          <div className={styles.datePickerPopup}>
            <h4>Filter by Date Range</h4>
            <div className={styles.datePickerInputs} style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <label style={{ flex: 1, minWidth: '180px' }}>
                Start Date
                <input type="date" value={tempStartDate} onChange={(e) => setTempStartDate(e.target.value)} style={{ width: '100%', marginTop: '4px' }} />
              </label>
              <label style={{ flex: 1, minWidth: '180px' }}>
                End Date
                <input type="date" value={tempEndDate} onChange={(e) => setTempEndDate(e.target.value)} style={{ width: '100%', marginTop: '4px' }} />
              </label>
            </div>
            <div className={styles.datePickerActions} style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className={styles.applyBtn} onClick={applyCustomRange}>Apply Filter</button>
              <button className={styles.cancelBtn} onClick={() => setShowGlobalDatePicker(false)}>Cancel</button>
              {(customStartDate || customEndDate) && (
                <button className={styles.clearBtn} onClick={() => {
                  setCustomStartDate(''); setCustomEndDate(''); setTempStartDate(''); setTempEndDate('');
                  setFilterStartDate(''); setFilterEndDate(''); setSelectedFilter('All'); handleTimeframeChange('30D'); setShowGlobalDatePicker(false);
                }}>Clear</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team Users Modal */}
      {selectedTeamUsersModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Users - {selectedTeamUsersModal.teamName}</h3>
              <button 
                onClick={() => setSelectedTeamUsersModal(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af' }}
              >
                ×
              </button>
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              {selectedTeamUsersModal.users.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {selectedTeamUsersModal.users.map((user, idx) => (
                    <li key={idx} style={{ padding: '12px', borderBottom: idx < selectedTeamUsersModal.users.length - 1 ? '1px solid #f3f4f6' : 'none', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{user.name}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{user.email}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px' }}>No users found</div>
              )}
            </div>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button 
                onClick={() => setSelectedTeamUsersModal(null)}
                style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.dashboardLayout}>
        <div className={styles.mainContent}>
          {/* Organization Analytics Card */}
          <div className={styles.analyticsCard}>
            <div className={styles.cardHeader}>
              <div className={styles.timeframeSelector}>
                <span className={styles.timeframeLabel}>Timeframe:</span>
                <div className={styles.timeframeButtons}>
                  {['30D', '90D', '12M', 'All', 'Custom'].map((tf) => (
                    <button key={tf} className={`${styles.timeframeBtn} ${selectedTimeframe === tf ? styles.activeTimeframe : ''}`} onClick={() => handleTimeframeChange(tf)}>
                      {tf}
                    </button>
                  ))}
                </div>
                {timeframeType === 'custom' && (
                  <div className={styles.customDateRange} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
                    <input type="date" className={styles.dateInput} value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ccc' }} />
                    <span>to</span>
                    <input type="date" className={styles.dateInput} value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ccc' }} />
                    <button className={styles.applyBtn} onClick={() => { if (customStartDate && customEndDate) { setTimeframeType('custom'); setSelectedTimeframe('Custom'); } else { alert('Please select both start and end dates.'); } }} style={{ padding: '0.25rem 0.75rem' }}>Apply</button>
                  </div>
                )}
              </div>
            </div>

            {analyticsEmptyHint && <div className={styles.analyticsEmptyHint} role="status">{analyticsEmptyHint}</div>}

            <div className={styles.statsGrid}>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Total Users</span><span className={styles.statValue}>{orgData.totalUsers}</span></div><Users className={styles.statIcon} size={25} color="#3b82f6" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Active (24h)</span><span className={styles.statValue}>{orgData.active24h}</span></div><Activity className={styles.statIcon} size={25} color="#f97316" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Last 24 hours</span><span className={styles.statValue}>{orgData.last24h}</span></div><Clock className={styles.statIcon} size={25} color="#8b5cf6" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Total Revenue</span><span className={styles.statValue}>${orgData.totalRevenue.toLocaleString()}</span><span className={styles.statChange}>+{orgData.revenueChange}%</span></div><DollarSign className={styles.statIcon} size={25} color="#10b981" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Total Profit</span><span className={styles.statValue}>${orgData.totalProfit.toLocaleString()}</span><span className={styles.statChange}>+{orgData.profitChange}%</span></div><TrendingUp className={styles.statIcon} size={25} color="#f97316" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Avg Rev/User</span><span className={styles.statValue}>${orgData.avgRevPerUser.toLocaleString()}</span></div><Users className={styles.statIcon} size={25} color="#3b82f6" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Delist Rate</span><span className={styles.statValue}>{orgData.delistRate}%</span></div><Percent className={styles.statIcon} size={25} color="#ef4444" /></div>
              <div className={styles.statCard}><div className={styles.statContent}><span className={styles.statLabel}>Avg Ticket Price</span><span className={styles.statValue}>${orgData.avgTicketPrice}</span></div><Ticket className={styles.statIcon} size={25} color="#8b5cf6" /></div>
            </div>
          </div>

          {/* Average Ticket Price by League */}
          <div className={styles.leagueSection}>
            <h3 className={styles.sectionTitle}>Average Ticket Price by League</h3>
            <div className={styles.leagueGrid}>
              {ticketPrices.map((item, index) => (
                <div key={index} className={styles.leagueCard}>
                  <div className={styles.leagueIcon}>{item.icon}</div>
                  <div className={styles.leagueInfo}><span className={styles.leagueName}>{item.league}</span><span className={styles.leaguePrice} style={{ color: item.color }}>${item.price}</span></div>
                  <span className={styles.leagueSold}>{item.sold} sold</span>
                </div>
              ))}
            </div>
          </div>

          {/* League Breakdown */}
          <div className={styles.leagueSection}>
            <h3 className={styles.sectionTitle}>League Breakdown (Sales)</h3>
            <div className={styles.leagueGrid}>
              {leagueSalesBreakdown.length > 0 ? leagueSalesBreakdown.map((item, index) => (
                <div key={index} className={styles.leagueCard}>
                  <div className={styles.leagueIcon}>{item.icon}</div>
                  <div className={styles.leagueInfo}><span className={styles.leagueName}>{item.league}</span><span className={styles.leaguePrice} style={{ color: item.color }}>{item.count} sales</span></div>
                  <span className={styles.leagueSold}>${(item.revenue / 1000).toFixed(1)}k</span>
                </div>
              )) : <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#6b7280' }}>No sales data available for the selected filters.</div>}
            </div>
          </div>

          {/* Client Type Breakdown */}
          <div className={styles.twoColumnGrid}>
            <div className={styles.distributionCard}>
              <h3 className={styles.cardTitle}><Users className={styles.cardIcon} size={18} color="#3b82f6" /> Client Type Breakdown</h3>
              <br />
              {isPricingModeLoadingInitial && <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>Loading pricing split...</div>}
              {clientTypeData.map((item, index) => (
                <div key={index} className={styles.distributionItem}>
                  <div className={styles.distributionHeader}><span>{item.type}</span><span>{item.count} ({item.percentage}%)</span></div>
                  <div className={styles.progressBar}><div className={styles.progressFill} style={{ width: `${item.percentage}%`, backgroundColor: item.color }} /></div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Performers and High Delist Users */}
          <div className={styles.twoColumnGrid}>
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.cardTitle}><TrendingUp className={styles.cardIcon} size={18} color="#3b82f6" /> Top Performers</h3>
                <select className={styles.filterSelect} value={topPerformersCount} onChange={(e) => setTopPerformersCount(Number(e.target.value))} style={{ marginLeft: 'auto' }}>
                  <option value={10}>Show 10</option><option value={20}>Show 20</option><option value={30}>Show 30</option><option value={40}>Show 40</option><option value={50}>Show 50</option>
                </select>
              </div>
              <div className={styles.tableContainer} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className={styles.table}>
                  <thead><tr><th>User</th><th>Tier</th><th>Revenue</th><th>Sold</th></tr></thead>
                  <tbody>
                    {topPerformers.map((performer, index) => (
                      <tr key={index}>
                        <td><div className={styles.userCell}><span className={styles.userRank}>#{performer.rank}</span><div className={styles.userAvatar} style={{ backgroundColor: performer.tierColor + '20', color: performer.tierColor }}>{performer.avatar}</div><div><div className={styles.userName}>{performer.name}</div><div className={styles.userEmail}>{performer.email}</div></div></div></td>
                        <td><span className={styles.tierBadge} style={{ backgroundColor: performer.tierColor + '20', color: performer.tierColor }}>{performer.tier}</span></td>
                        <td className={styles.revenue}>${performer.revenue.toLocaleString()}</td>
                        <td>{performer.sold}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.cardTitle}><AlertTriangle className={styles.cardIcon} size={18} color="#ef4444" /> High Delist Rate Users</h3>
                <select className={styles.filterSelect} value={highDelistCount} onChange={(e) => setHighDelistCount(Number(e.target.value))} style={{ marginLeft: 'auto' }}>
                  <option value={10}>Show 10</option><option value={20}>Show 20</option><option value={30}>Show 30</option><option value={40}>Show 40</option><option value={50}>Show 50</option>
                </select>
              </div>
              <div className={styles.tableContainer} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className={styles.table}>
                  <thead><tr><th>User</th><th>Type</th><th>Delist Rate</th><th>Delist Count</th></tr></thead>
                  <tbody>
                    {highDelistUsers.map((user, index) => (
                      <tr key={index}>
                        <td><div><div className={styles.userName}>{user.name}</div><div className={styles.userEmail}>{user.email}</div></div></td>
                        <td>{user.type}</td>
                        <td className={styles.delistRate}>{user.delistRate}%</td>
                        <td>{user.delistCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ===== TEAMS SELECTED BY USERS ===== */}
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <h3 className={styles.cardTitle}><Users className={styles.cardIcon} size={18} color="#3b82f6" /> Teams Selected by Users</h3>
              <div className={styles.tableActions}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '12px' }}>
                  <input
                    type="date"
                    value={teamsStartDate}
                    onChange={(e) => setTeamsStartDate(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: 'transparent' }}
                    placeholder="Start"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={teamsEndDate}
                    onChange={(e) => setTeamsEndDate(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: 'transparent' }}
                    placeholder="End"
                  />
                  {(teamsStartDate || teamsEndDate) && (
                    <button
                      onClick={() => { setTeamsStartDate(''); setTeamsEndDate(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <select className={styles.filterSelect} value={teamsLeagueFilter} onChange={(e) => setTeamsLeagueFilter(e.target.value)} style={{ marginRight: '12px' }}>
                  {availableLeaguesForTeams.map((league) => (
                    <option key={league.id} value={league.id}>{league.name}</option>
                  ))}
                </select>

                <div className={styles.searchBox}>
                  <SearchIcon size={14} className={styles.searchIcon} />
                  <input 
                    type="text" 
                    placeholder="Search teams..." 
                    className={styles.searchInput} 
                    value={teamsSearchQuery} 
                    onChange={(e) => setTeamsSearchQuery(e.target.value)} 
                  />
                </div>

                <button 
                  className={styles.exportBtn} 
                  onClick={handleExportTeams} 
                  disabled={!filteredTeamsData.length} 
                  style={!filteredTeamsData.length ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <Download size={14} /> Export
                </button>
              </div>
            </div>

            {userPreferencesLoading || sportsTeamsLoading || allLeaguesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ marginBottom: '8px' }}>Loading teams data...</div>
              </div>
            ) : filteredTeamsData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ marginBottom: '8px' }}>No teams found for the selected filters</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {teamsLeagueFilter !== 'All Leagues'
                    ? 'Try a different league or clear the search filter.'
                    : 'No sports teams are available in the database.'}
                </div>
              </div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.teamsTable}>
                  <thead>
                    <tr>
                      <th>TEAM</th>
                      <th>LEAGUE</th>
                      <th>USERS</th>
                      <th>ACTIVE TICKETS</th>
                      <th>TOTAL LISTED</th>
                      <th># TICKETS SOLD</th>
                      <th>TOTAL SALE PRICE</th>
                      <th>TOTAL PAID OUT</th>
                      <th>POTENTIAL PAYOUT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeamsData.map((team, index) => (
                      <tr key={index}>
                        <td className={styles.teamName}>{String(team.team || 'Unknown Team')}</td>
                        <td>{String(team.league || 'Unknown')}</td>
                        <td 
                          style={{ cursor: team.userDetails.length > 0 ? 'pointer' : 'default', color: team.userDetails.length > 0 ? '#3b82f6' : '#6b7280' }}
                          onClick={() => {
                            if (team.userDetails.length > 0) {
                              setSelectedTeamUsersModal({ 
                                teamName: String(team.team || 'Unknown Team'), 
                                users: team.userDetails 
                              });
                            }
                          }}
                          title={team.userDetails.map(u => u.name).join('\n')}
                        >
                          {team.usersCount}
                        </td>
                        <td>{team.active}</td>
                        <td>${String(team.listed.toLocaleString())}</td>
                        <td>{team.sold}</td>
                        <td>${String(team.salePrice.toLocaleString())}</td>
                        <td>${String(team.paidOut.toLocaleString())}</td>
                        <td className={styles.potential}>${String(team.potential.toLocaleString())}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className={styles.tableFooter}>
                  <div className={styles.footerTotal}>
                    <span className={styles.totalLabel}>Total:</span>
                    <span></span>
                    <span>{teamTotals.users}</span>
                    <span>{teamTotals.active}</span>
                    <span>${String(teamTotals.listed.toLocaleString())}</span>
                    <span>{teamTotals.sold}</span>
                    <span>${String(teamTotals.salePrice.toLocaleString())}</span>
                    <span>${String(teamTotals.paidOut.toLocaleString())}</span>
                    <span className={styles.potential}>${String(teamTotals.potential.toLocaleString())}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Marketplace Performance */}
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <h3 className={styles.cardTitle}>Marketplace Performance</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="date"
                    value={marketplaceStartDate}
                    onChange={(e) => setMarketplaceStartDate(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: 'transparent' }}
                    placeholder="Start"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={marketplaceEndDate}
                    onChange={(e) => setMarketplaceEndDate(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: 'transparent' }}
                    placeholder="End"
                  />
                  {(marketplaceStartDate || marketplaceEndDate) && (
                    <button
                      onClick={() => { setMarketplaceStartDate(''); setMarketplaceEndDate(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {finalMarketplaceData.length > 0 
                    ? `${finalMarketplaceData.reduce((sum, m) => sum + m.tickets, 0)} tickets sold across ${finalMarketplaceData.length} marketplace(s)`
                    : (marketplaceStartDate || marketplaceEndDate)
                      ? '0 tickets sold for selected marketplace date range'
                      : `Total tickets: ${scopedTimeline.length}`
                  }
                </div>
              </div>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>MARKETPLACE</th>
                    <th># TICKETS SOLD</th>
                    <th>TOTAL SALE PRICE</th>
                    <th>TOTAL PAID OUT</th>
                    <th>QA PROFIT</th>
                  </tr>
                </thead>
                <tbody>
                  {finalMarketplaceData.length > 0 ? (
                    <>
                      {finalMarketplaceData.map((item, index) => (
                        <tr key={`${item.name}-${index}`}>
                          <td>
                            <div className={styles.marketplaceCell}>
                              <span className={styles.marketplaceDot} style={{ backgroundColor: item.color }} />
                              {item.name}
                            </div>
                          </td>
                          <td>{item.tickets.toLocaleString()}</td>
                          <td>${item.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>${item.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={styles.profit}>${item.profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 600, backgroundColor: 'rgba(0,0,0,0.02)' }}>
                        <td>TOTAL</td>
                        <td>{finalMarketplaceData.reduce((sum, m) => sum + m.tickets, 0).toLocaleString()}</td>
                        <td>${finalMarketplaceData.reduce((sum, m) => sum + m.revenue, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>${finalMarketplaceData.reduce((sum, m) => sum + m.paid, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className={styles.profit}>${finalMarketplaceData.reduce((sum, m) => sum + m.profit, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        <AlertCircle size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                        <div>No sold tickets found for the selected timeframe</div>
                        <div style={{ fontSize: '12px', marginTop: '8px', color: '#9ca3af' }}>
                          Marketplace data only shows tickets with:
                        </div>
                        <div style={{ fontSize: '11px', marginTop: '6px', color: '#9ca3af', fontStyle: 'italic' }}>
                          • Sold status • Valid marketplace name • Price amount greater than $0
                        </div>
                        {scopedTimeline.length > 0 && (
                          <div style={{ marginTop: '12px', fontSize: '11px', color: '#f97316' }}>
                            Tip: {scopedTimeline.length} ticket(s) loaded - check if they have marketplace names or sold status.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lead Time Analytics */}
          <div className={styles.leadTimeSection}>
            <h3 className={styles.sectionTitle}>Lead Time Analytics</h3>
            
            <div className={styles.leadTimeCards}>
              <div className={styles.leadTimeCard}><h4 className={styles.leadTimeCardTitle}>Sold Lead Time</h4><p className={styles.leadTimeCardText}>{leadTimeData.soldLeadTime}</p></div>
              <div className={styles.leadTimeCard}><h4 className={styles.leadTimeCardTitle}>Listed Lead Time</h4><p className={styles.leadTimeCardText}>{leadTimeData.listedLeadTime}</p></div>
            </div>

            <div className={styles.leadTimeLeagueContainer}>
              <h4 className={styles.leadTimeSubtitle}>Lead Time by League</h4>
              <ul className={styles.leadTimeLeagueList}>{leadTimeData.leadTimeByLeague.map((item, index) => <li key={index}>• {item.league} tickets sell ~{item.days} days before the event on average.</li>)}</ul>
              <p className={styles.leadTimeNote}>Lead time = days between action (listed/sold) and event start. Lower values indicate closer-to-event activity.</p>
            </div>

            <h4 className={styles.leadTimeSubtitle}>Timing & Pricing Intelligence</h4>
            <div className={styles.intelligenceGrid}>
              <div className={styles.intelligenceCard}><span className={styles.intelligenceValue}>{leadTimeData.avgDaysBeforeSold}</span><span className={styles.intelligenceLabel}>Platform Avg Days Before Sold</span></div>
              <div className={styles.intelligenceCard}><span className={styles.intelligenceValue}>{leadTimeData.avgDaysBeforeListed}</span><span className={styles.intelligenceLabel}>Platform Avg Days Before Listed</span></div>
              <div className={styles.intelligenceCard}><span className={styles.intelligenceValue}>{`${leadTimeData.avgPriceChange >= 0 ? '+' : '-'}$${Math.abs(leadTimeData.avgPriceChange).toFixed(2)}`}</span><span className={styles.intelligenceLabel}>Avg Price Change</span></div>
              <div className={styles.intelligenceCard}><span className={styles.intelligenceValue}>{leadTimeData.totalTicketsAnalyzed}</span><span className={styles.intelligenceLabel}>Total Tickets Analyzed</span></div>
            </div>

            <div className={styles.platformTimingSection}>
              <h4 className={styles.leadTimeSubtitle}>Platform Sale Timing Distribution</h4>
              <div className={styles.timingBars}>{displayedPlatformSaleTiming.map((value, index) => <div key={index} className={styles.timingBarContainer}><div className={styles.timingBarFill} style={{ height: `${value * 2}px` }} /><span className={styles.timingBarValue}>{value}%</span></div>)}</div>
              <div className={styles.timingLabels}><span>3 Days Before</span><span>7 Days Before</span><span>1-2 Weeks</span><span>2-4 Weeks</span><span>After Event</span></div>
            </div>

            <h4 className={styles.leadTimeSubtitle}>User Sale Timing Comparison</h4>
            <div className={styles.platformAvg}>Platform Avg: {leadTimeData.avgDaysBeforeSold} days</div>
            
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead><tr><th>USER</th><th>SOLD</th><th>AVG DAYS ↑</th><th>VS PLATFORM</th><th>PRICE CHANGE</th><th>PATTERN</th></tr></thead>
                <tbody>
                  {filteredUserSaleTimingData.map((user, index) => (
                    <tr key={`${user.email}-${index}`} onClick={() => setUserSaleTimingDetailEmail(user.email)} style={{ cursor: 'pointer', backgroundColor: user.email === userSaleTimingDetail?.email ? 'rgba(79, 142, 247, 0.08)' : undefined }}>
                      <td><div><div className={styles.userName}>{user.name}</div><div className={styles.userEmail}>{user.email}</div></div></td>
                      <td>{user.sold}</td>
                      <td>{user.avgDays}</td>
                      <td className={user.vs < 0 ? styles.positive : styles.negative}>{user.vs > 0 ? '+' : ''}{user.vs} days</td>
                      <td className={user.priceChange < 0 ? styles.negative : styles.positive}>{user.priceChange > 0 ? '+' : ''}{user.priceChange}%</td>
                      <td><span className={`${styles.patternBadge} ${user.pattern.includes('week') ? styles.weekBefore : styles.earlySeller}`}>{user.pattern}</span></td>
                    </tr>
                  ))}
                  {filteredUserSaleTimingData.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280', padding: '14px' }}>No user timing rows match current pattern/price filters.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className={styles.timingFilterSection}>
              <button className={styles.filterBtn}><Filter size={14} /> Filter</button>
              <select className={styles.filterSelect} value={selectedPattern} onChange={(e) => { setUserSaleTimingDetailEmail(null); setSelectedPattern(e.target.value); }}>
                <option>All Patterns</option><option>Week before seller</option><option>Early seller</option>
              </select>
              <select className={styles.filterSelect} value={selectedPriceChg} onChange={(e) => { setUserSaleTimingDetailEmail(null); setSelectedPriceChg(e.target.value); }}>
                <option>All Price Chg</option><option>Positive</option><option>Negative</option>
              </select>
              <button type="button" className={styles.exportBtn} onClick={handleExportUserSaleTiming} disabled={!filteredUserSaleTimingData.length} style={!filteredUserSaleTimingData.length ? { opacity: 0.5, cursor: 'not-allowed' } : {}}><Download size={14} /> Export</button>
            </div>

            {userSaleTimingDetail && (
              <div className={styles.userDetailCard}>
                <div className={styles.userDetailHeader}><span className={styles.userDetailName}>{userSaleTimingDetail.name}</span><span className={styles.userDetailEmail}>{userSaleTimingDetail.email}</span></div>
                <div className={styles.userDetailStats}>
                  <div className={styles.userDetailStat}><span className={styles.userDetailLabel}>Sold</span><span className={styles.userDetailValue}>{userSaleTimingDetail.sold}</span></div>
                  <div className={styles.userDetailStat}><span className={styles.userDetailLabel}>Avg Days</span><span className={styles.userDetailValue}>{userSaleTimingDetail.avgDays}</span></div>
                  <div className={styles.userDetailStat}><span className={styles.userDetailLabel}>VS Platform</span><span className={`${styles.userDetailValue} ${userSaleTimingDetail.vs < 0 ? styles.positive : styles.negative}`}>{userSaleTimingDetail.vs > 0 ? '+' : ''}{userSaleTimingDetail.vs} days</span></div>
                  <div className={styles.userDetailStat}><span className={styles.userDetailLabel}>Price Change</span><span className={`${styles.userDetailValue} ${userSaleTimingDetail.priceChange < 0 ? styles.negative : styles.positive}`}>{userSaleTimingDetail.priceChange > 0 ? '+' : ''}{userSaleTimingDetail.priceChange}%</span></div>
                  <div className={styles.userDetailStat}><span className={styles.userDetailLabel}>Pattern</span><span className={`${styles.patternBadge} ${userSaleTimingDetail.pattern.includes('week') ? styles.weekBefore : styles.earlySeller}`}>{userSaleTimingDetail.pattern}</span></div>
                </div>
              </div>
            )}

            <h4 className={styles.leadTimeSubtitle}>Listing Timing Distribution</h4>
            <div className={styles.totalListings}>{leadTimeData.totalListings} total listings</div>
            <div className={styles.listingTimingGrid}>
              {leadTimeData.listingTimingDistribution.map((item, index) => (
                <div key={index} className={styles.listingTimingItem}>
                  <div className={styles.listingTimingHeader}><span>{item.timing}</span><span className={styles.listingTimingPercentage}>{item.percentage}%</span></div>
                  <div className={styles.listingTimingBar}><div className={styles.listingTimingFill} style={{ width: `${item.percentage}%` }} /></div>
                  <div className={styles.listingTimingDetails}><span>{item.count} listings</span><span>${item.price}</span></div>
                </div>
              ))}
            </div>

            <h4 className={styles.leadTimeSubtitle}>Timing by League</h4>
            <div className={styles.timingLeagueGrid}>
              {leadTimeData.timingByLeague.map((item, index) => (
                <div key={index} className={styles.timingLeagueCard}>
                  <div className={styles.timingLeagueHeader}>{item.league}</div>
                  <div className={styles.timingLeagueStats}>
                    <div>
                      <span className={styles.timingLeagueValue}>{item.daysToSell}</span>
                      <span className={styles.timingLeagueLabel}>Days to Sell</span>
                    </div>
                    <div>
                      <span className={`${styles.timingLeagueValue} ${item.priceChg.includes('+') ? styles.positive : styles.negative}`}>{item.priceChg}</span>
                      <span className={styles.timingLeagueLabel}>Price Chg</span>
                    </div>
                  </div>
                  <div className={styles.timingLeagueSold}>{item.sold} sold</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;